import { FormikProps } from "formik"
import React, { forwardRef, Suspense, useRef } from "react"
import { MERCHANT_ADMIN_ROUTE, MERCHANT_ADMIN_SETTINGS_VAT_CODES_ROUTE } from "../../../../../constants/routes"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"

import { useNavigate } from "react-router"
import { useParams } from "react-router-dom"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { CreateOrEditVatCodesFormValues } from "../vatCodesTypes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { editVatCodeApi, fetchDetailedVatCode, FetchDetailedVatCodeResponse, VatCodesQuery } from "../vatCodesApi"
import { CreateUpdateVatCodeForm } from "../components/CreateUpdateVatCodeForm"

function transformData({ vatCode }: FetchDetailedVatCodeResponse): CreateOrEditVatCodesFormValues {
    const amount = vatCode.vatPercentage.amount / Math.pow(10, vatCode.vatPercentage.scale)

    return {
        name: vatCode.name,
        code: vatCode.code,
        vatPercentage: {
            amount,
            scale: vatCode.vatPercentage.scale,
        },
    }
}

export function EditVatCode() {
    const formRef = useRef<FormikProps<CreateOrEditVatCodesFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="merchant-vat-codes-edit-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_EDIT_VAT_CODES_LIST}
                    title="Edit VAT code"
                    rightElement={
                        <LocalizedStrict id="merchant-vat-codes-save-button">
                            <StandardButton onClick={handleSaveClick}>Create</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-settings-header">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-vat-codes">
                            <BreadcrumbLink to={MERCHANT_ADMIN_SETTINGS_VAT_CODES_ROUTE}>VAT codes</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-edit-vat-code">
                            <Breadcrumb>Edit VAT code</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <VatCode ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const VatCode = forwardRef<FormikProps<CreateOrEditVatCodesFormValues>>((props, ref) => {
    const navigate = useNavigate()
    const { vatCodeId } = useParams()
    const vatCodeIdNumber = Number(vatCodeId)

    const detailedVatCode = useApiQuery(fetchDetailedVatCode, {
        queryName: VatCodesQuery.DETAILS,
        dependencies: {
            id: vatCodeIdNumber ?? 0,
        },
        enabled: !!vatCodeIdNumber,
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
    })

    const { mutate: callUpdateVatCodes } = useApiMutation(editVatCodeApi, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [VatCodesQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(VatCodesQuery.DETAILS, { id: vatCodeIdNumber }),
            })
            navigate(MERCHANT_ADMIN_SETTINGS_VAT_CODES_ROUTE)
        },
    })

    const editVatCode = (data: CreateOrEditVatCodesFormValues) => {
        const vatCode = {
            code: data.code,
            name: data.name,
            vatPercentage: {
                amount: (data.vatPercentage.amount ?? 0) * Math.pow(10, 1),
                scale: data.vatPercentage.scale,
            },
        }
        callUpdateVatCodes({ id: vatCodeIdNumber, vatCode })
    }

    return <CreateUpdateVatCodeForm ref={ref} initialValues={detailedVatCode.data} onSubmit={editVatCode} />
})
